import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { selectUser } from 'selectors';
import styled from 'styled-components';
import { Button, Container, Image, Text } from 'styled-minimal';

import { useAppSelector } from 'modules/hooks';
import { STATUS } from 'literals';

import { login, setEmail, setName } from 'actions';

import Background from 'components/Background';
import Footer from 'components/Footer';
import WelcomeModal from 'components/WelcomeModal/WelcomeModal';
import AboutModal from 'components/AboutModal/AboutModal';

const StyledContainer = styled(Container)`
  padding: 100px;
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  background-color: rgb(55, 65, 81);

  @media (max-width: 768px) {
    padding: 24px 20px 80px;
    flex-direction: column;
    align-items: stretch;
    min-height: 100vh;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 30px;
  flex-direction: column;
  font-size: 1.6rem;
  color: white;

  @media (max-width: 768px) {
    width: 100%;
    gap: 18px;
    align-items: stretch;
  }
`;

const StyledButton = styled(Button)`
  width: 142px;
  height: 40px;
  font-size: 1.6rem;

  &:disabled {
    opacity: 0.7;
    cursor: progress;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 48px;
    font-size: 1.6rem;
  }

  border-radius: 4px;
  border: 2px solid white;
  text-transform: initial;
  display: flex;
  justify-content: center;
  color: black;
  background-color: white;
  box-shadow: 5px 5px 5px darken(rgba(red(#81c784), green(#81c784), blue(#81c784), 0.2), 2%),
    -5px -5px 5px lighten(rgba(red(#81c784), green(#81c784), blue(#81c784), 0.2), 2%);
  transition: ease all 0.3s;
  cursor: pointer;

  &:hover {
    box-shadow: 10px 10px 10px darken(rgba(red(#81c784), green(#81c784), blue(#81c784), 0.1), 2%),
      -10px -10px 10px lighten(rgba(red(#81c784), green(#81c784), blue(#81c784), 0.1), 2%);
    transform: translateY(-3px);
  }
  &:active {
    transform: scale(0.9);
    box-shadow: inset -1px -1px 3px
        darken(rgba(red(#81c784), green(#81c784), blue(#81c784), 0.3), 2%),
      -10px -10px 10px lighten(rgba(red(#81c784), green(#81c784), blue(#81c784), 0), 2%);
  }
`;

const LeftSide = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 150px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const RightSide = styled.div`
  position: absolute;
  right: 20px;
  top: 0;
  bottom: 20px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    display: none;
  }
`;

// The desktop venn diagram is large and decorative — hide it on small screens
// so the form column owns the viewport.
const VennImage = styled(Image)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const Heading = styled(Text)`
  color: rgb(55, 65, 81);
  text-shadow: -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff;
  font-size: 120px;
  font-weight: 100;
  line-height: 30px;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 56px;
    line-height: 1;
    text-align: center;
  }
`;

const Title = styled(Text)`
  font-size: 32px;
  color: white;
  text-transform: uppercase;
  line-height: 0px;
  margin-bottom: 50px;

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 1.3;
    margin: 12px 0 20px;
    text-align: center;
  }
`;

const Label = styled(Text)`
  color: white;
  font-weight: 100;
  line-height: 0px;

  @media (max-width: 768px) {
    line-height: 1.4;
  }
`;

const NameInput = styled.input`
  padding: 12px;
  font-size: 1.4rem;
  margin-bottom: 12px;
  outline: none;
  border-radius: 8px;
  border: none;

  @media (max-width: 768px) {
    width: 100%;
    box-sizing: border-box;
    /* >=16px font keeps iOS Safari from zooming into the input on focus */
    font-size: 16px;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;

  @media (max-width: 768px) {
    justify-content: stretch;
  }
`;

const ErrorMessage = styled.div`
  color: #ffd6d6;
  background: rgba(160, 30, 30, 0.35);
  border: 1px solid rgba(255, 200, 200, 0.4);
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 1.3rem;
  line-height: 1.4;
  margin-top: 12px;
  /* Ensure long messages wrap inside the form column on every viewport */
  max-width: 100%;
  word-wrap: break-word;
`;

const AboutLink = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  font-size: 1.2rem;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  padding: 0;
  align-self: center;

  &:hover { color: #fff; }
`;

function Home() {
  const dispatch = useDispatch();
  const user = useAppSelector(selectUser);
  const { name, email, loginError, status } = user;
  const isLoggingIn = status === STATUS.RUNNING;
  const nameInput = useRef<HTMLInputElement>(null);
  const emailInput = useRef<HTMLInputElement>(null);

  const [showWelcome, setShowWelcome] = useState(false);
  const [showAbout, setShowAbout] = useState(false);

  // Show welcome modal once per session
  useEffect(() => {
    if (!sessionStorage.getItem('ik_welcome_shown')) {
      sessionStorage.setItem('ik_welcome_shown', '1');
      setShowWelcome(true);
    }
  }, []);

  const handleClickLogin = (coach: number) => {
    // Block re-entry while a previous attempt is in flight so mashing the
    // button doesn't enqueue concurrent start-session requests.
    if (isLoggingIn) return;
    if (!name) return nameInput.current?.focus();
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email))
      return emailInput.current?.focus();
    dispatch(login(coach, false));
  };

  return (
    <>
    <Background key="Home" data-testid="Home">
      <StyledContainer fullScreen>
        <LeftSide>
          <Image
            alt="star"
            src={`${process.env.PUBLIC_URL}/media/images/star.png`}
            style={{ position: 'absolute', top: '60px' }}
            width={80}
          />
          <Image
            alt="triangles"
            src={`${process.env.PUBLIC_URL}/media/images/triangles.png`}
            width={30}
          />
          <Image
            alt="circles"
            src={`${process.env.PUBLIC_URL}/media/images/circles.png`}
            style={{ position: 'absolute', bottom: '-60px', left: '-60px' }}
            width={200}
          />
        </LeftSide>
        <ButtonContainer>
          <Heading>Ikigai</Heading>
          <hr style={{ width: '100%' }} />
          <Title>A reason for being</Title>
          <Label>What's your name?</Label>
          <NameInput
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              dispatch(setName(event.target.value))
            }
            type="text"
            value={name}
            placeholder="Type your name here"
            ref={nameInput}
          />
          <Label>What's your email address?</Label>
          <NameInput
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              dispatch(setEmail(event.target.value))
            }
            type="text"
            value={email}
            placeholder="Type your email address here"
            ref={emailInput}
          />
          <Buttons>
            <StyledButton
              data-testid="MentorMirror"
              onClick={() => handleClickLogin(0)}
              disabled={isLoggingIn}
              aria-busy={isLoggingIn}
              // size="xl"
              textTransform="uppercase"
              variant="primary"
            >
              <Text>{isLoggingIn ? 'Starting…' : loginError ? 'Try Again' : 'Begin Chat'}</Text>
            </StyledButton>
          </Buttons>
          {loginError && (
            <ErrorMessage role="alert">
              {loginError}
              <br />
              Click <strong>Try Again</strong> — no need to refresh.
            </ErrorMessage>
          )}
          <AboutLink onClick={() => setShowAbout(true)}>About this interview</AboutLink>
        </ButtonContainer>
        <VennImage alt="venn" src={`${process.env.PUBLIC_URL}/media/images/venn.svg`} width={500} />
        <RightSide>
          <div style={{ flex: 1 }} />
          <Image
            alt="purple_dots"
            src={`${process.env.PUBLIC_URL}/media/images/purple_dots.png`}
            style={{ alignSelf: 'end', marginRight: '8px' }}
            width={16}
          />
          <div style={{ flex: 1 }} />
          <Image
            alt="white_dots"
            src={`${process.env.PUBLIC_URL}/media/images/white_dots.png`}
            width={400}
          />
        </RightSide>
      </StyledContainer>
      <Footer style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />
    </Background>

    {showWelcome && <WelcomeModal onDismiss={() => setShowWelcome(false)} />}
    {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}
    </>
  );
}

export default Home;
